import { CoreEntityDto } from '@/interfaces/core.interface'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useCoreEntityStore } from './coreEntity'
import { useStore } from 'vuex'
import { COMPLEX_PROPERTIES } from '@/interfaces/complex.interface'
import { BUILDING_PROPERTIES } from '@/interfaces/building.interface'
import { LEVEL_PROPERTIES } from '@/interfaces/level.interface'
import { ROOM_PROPERTIES } from '@/interfaces/room.interface'
import { REAL_ESTATE_PROPERTIES } from '@/interfaces/realEstate.interface'

type OldSelectionState = {
  clicked:
    | 'region'
    | 'complex'
    | 'realEstate'
    | 'building'
    | 'level'
    | 'room'
    | 'poi'
    | 'line'
    | 'zone'
  complex: COMPLEX_PROPERTIES | undefined
  realEstate: REAL_ESTATE_PROPERTIES | undefined
  building: BUILDING_PROPERTIES | undefined
  level: LEVEL_PROPERTIES | undefined
  room: ROOM_PROPERTIES | undefined
}

export type ClickedEntityType =
  | 'REGION'
  | 'COMPLEX'
  | 'REAL_ESTATE'
  | 'CONSTRUCTION'
  | 'LEVEL'
  | 'SPACE'
  | 'POI'
  | 'LINE'
  | 'ZONE'

export const useSelectionStore = defineStore('selection', () => {
  const store = useStore()
  const { getCoreEntity } = useCoreEntityStore()

  const currentLoading = ref(false)
  const currentEntity = ref<CoreEntityDto>()

  const state = computed<OldSelectionState>(() => ({
    clicked: store.state.clickedItemType,
    complex: store.state.mapview.selectedComplex,
    realEstate: store.state.mapview.selectedRealEstate,
    building: store.state.mapview.selectedBuilding,
    level: store.state.selectedLevel,
    room: store.state.selectedRoom
  }))

  // Getters
  const clickedEntityType = computed<ClickedEntityType>(() => {
    switch (state.value.clicked) {
      case 'complex':
        return 'COMPLEX'
      case 'realEstate':
        return 'REAL_ESTATE'
      case 'building':
        return 'CONSTRUCTION'
      case 'level':
        return 'LEVEL'
      case 'room':
        return 'SPACE'
      case 'poi':
        return 'POI'
      case 'line':
        return 'LINE'
      case 'zone':
        return 'ZONE'
      default:
        return 'REGION'
    }
  })

  // Watches

  watch(
    state,
    async () => {
      const selectedId = getSelectedId(state.value)
      if (selectedId) {
        currentLoading.value = true
        const entity = await getCoreEntity(selectedId)
        currentEntity.value = entity
        currentLoading.value = false
      } else {
        currentEntity.value = undefined
      }
    },
    { immediate: true }
  )

  return {
    clickedEntityType,
    currentEntity,
    currentLoading
  }
})

function getSelectedId(state: OldSelectionState): string | undefined {
  let id: string | undefined
  switch (state.clicked) {
    case 'complex':
      id = state.complex?.belongsToId
      break
    case 'realEstate':
      id = state.realEstate?.id
      break
    case 'building':
      id = state.building?.id
      break
    case 'level':
      id = state.level?.id
      break
    case 'room':
      id = state.room?.id
      break
    default:
      id = undefined
      break
  }
  return id
}
